import { sql } from "drizzle-orm";
import {
  check,
  index,
  integer,
  sqliteTable,
  text,
  uniqueIndex,
} from "drizzle-orm/sqlite-core";
import { timestamps } from "@/db/columns.helpers";
import { user } from "@/db/schema/auth";
import { hiive } from "@/db/schema/hiive";
import type { PaymentProvider } from "@/db/schema/payment";
import type { PromotionConstraints } from "@/db/schema/promotion";
import type { RefundState } from "@/db/schema/refund";
import { taxProfile } from "@/db/schema/tax";
import type { Rule } from "@/db/schema/wallet-card";
import type { PricingSnapshot } from "@/lib/pricing/types";
import type { TaxSnapshot } from "@/lib/tax/types";

// ──────────────────────────────────────────────────────────────────────────────
// PACKS — prepaid, metered entitlements
//
// "Money paid once, consumed many times, under rules." A 10-climb card, a
// 10-class MMA pack, an unlimited salsa week. See plans/hiive-packs.md.
//
// NOT to be confused with `event_pass_type` / `event_occurrence_pass` /
// `venue_experience_ticket`, which all mean "one ticket type". A pack BUYS
// those; it is not one of them. The word "pass" is taken — this is a Pack.
//
// FOUR IDEAS CARRY THE WHOLE DESIGN:
//
//   • A redemption is a TENDER, not a discount. The booking is priced and taxed
//     normally; the pack covers some of its lines and the residual goes to the
//     ordinary payment path. Tax settled at the pack sale (time of supply is the
//     receipt of the advance), so redemptions are zero-value, tax-neutral events.
//
//   • BALANCE IS NEVER STORED. It is derived from the ledger at runtime, which
//     makes it drift-proof and expiry-safe:
//
//       remaining(H) = H.creditsGranted − Σ |E.credits|
//         for entries E on H with kind IN ('hold','redeem','adjust')
//                             and status IN ('held','settled')
//
//     A `hold` counts as consumed the instant it lands. That is what makes
//     concurrent double-spend impossible, and it maps one-to-one onto the
//     reserve → confirm → expire lifecycle both booking engines already run.
//
//   • RULES ARE SNAPSHOTTED AT PURCHASE (`holding.snapshot`). A queen editing a
//     pack must never shrink what somebody already bought. Same discipline as
//     `taxSnapshot` and `AppliedPromotion`.
//
//   • CREDITS ARE COUNTS, NEVER RUPEES. A rupee balance spendable across hiives
//     is a semi-closed PPI instrument needing RBI authorisation. Counts are not
//     money, and sidestep it entirely.
// ──────────────────────────────────────────────────────────────────────────────

// credit: N credits, spent at will (the 10-climb card, the 10-class pack).
// window: unlimited-or-capped inside a date window (the salsa week pass).
export const PACK_KIND = ["credit", "window"] as const;
export type PackKind = (typeof PACK_KIND)[number];

// The single most consequential authoring choice in the feature, because it
// decides WHO HOLDS THE SEAT.
//   on_demand: the member books each session themselves. Seats stay sellable to
//              drop-ins until they do.
//   enrolled:  the purchase itself writes one booking per session and holds a
//              seat in each. The classic "course": one registration, whole
//              series. Those seats can no longer be sold.
//
// Required on a window pack, optional on a credit pack. An `enrolled` CREDIT
// pack is a batch/cohort pass: the purchase books the cycle, and any credits
// beyond it stay loose for sessions that do not exist yet (Salsa Nova's fifth
// credit, for a makeup class whose date nobody has picked).
export const PACK_REDEMPTION_MODE = ["on_demand", "enrolled"] as const;
export type PackRedemptionMode = (typeof PACK_REDEMPTION_MODE)[number];

// When the validity clock starts ticking.
//   first_use lets somebody buy in December for a January start, and lets them
//   book before the clock starts.
export const PACK_CLOCK_START = [
  "purchase",
  "first_use",
  "fixed_date",
] as const;
export type PackClockStart = (typeof PACK_CLOCK_START)[number];

export const PACK_VALIDITY_MODE = ["never", "duration", "fixed_date"] as const;
export type PackValidityMode = (typeof PACK_VALIDITY_MODE)[number];

// Rolling windows are measured from the clock start; fixed windows are the same
// calendar dates for everyone who buys ("week of 15 Sep").
export const PACK_WINDOW_MODE = ["rolling", "fixed"] as const;
export type PackWindowMode = (typeof PACK_WINDOW_MODE)[number];

export const PACK_STATUS = ["draft", "published", "archived"] as const;
export type PackStatus = (typeof PACK_STATUS)[number];

// What a scope row points at. Rows OR together: a pack valid at the venue AND on
// the hiive's events is simply two rows, which is the cross-sell win.
//   activity is the broad brush — "any bouldering, wherever this hiive runs it".
export const PACK_SCOPE_TARGET = [
  "venue_experience",
  "venue_experience_ticket",
  "event",
  "event_pass_type",
  "activity",
] as const;
export type PackScopeTarget = (typeof PACK_SCOPE_TARGET)[number];

// How a holding came to exist. `offline` is a cash sale at the front desk, which
// is how these actually sell here; `comp` is a queen giving one away.
export const PACK_HOLDING_SOURCE = [
  "purchase",
  "offline",
  "comp",
  "tier_grant",
] as const;
export type PackHoldingSource = (typeof PACK_HOLDING_SOURCE)[number];

export const PACK_HOLDING_STATUS = [
  "pending", // paid for but not yet captured
  "active",
  "frozen",
  "exhausted", // credits all spent — "the pack completes"
  "expired",
  "refunded",
  "void", // revoked: chargeback, or an admin unwinding a mistake
] as const;
export type PackHoldingStatus = (typeof PACK_HOLDING_STATUS)[number];

// Mirrors VENUE_BOOKING_PAYMENT_STATUS deliberately: `markSourceRefundState` and
// `bumpSourceTotals` in src/lib/refunds/service.ts write `paymentStatus:
// "refunded"` onto whatever source table they are given, so a holding has to
// speak the same vocabulary to ride the refund engine.
export const PACK_HOLDING_PAYMENT_STATUS = [
  "pending",
  "paid",
  "failed",
  "refunded",
  "not_required",
  "waived",
] as const;
export type PackHoldingPaymentStatus =
  (typeof PACK_HOLDING_PAYMENT_STATUS)[number];

// Movement kinds on the ledger.
//   hold    — placed at reserve; consumes immediately (the double-spend guard)
//   redeem  — a hold that settled, or a one-shot settle at the front desk
//   release — reverses a hold or redeem (cancel in window, booking expiry,
//             operator cancellation)
//   adjust  — admin correction, or a re-price after a reschedule
//   expire  — audit row for a lot the clock already excluded
//   refund  — credits returned to nobody: the holding was refunded for cash
//   revoke  — chargeback or admin void of the unspent remainder
export const PACK_LEDGER_KIND = [
  "hold",
  "redeem",
  "release",
  "adjust",
  "expire",
  "refund",
  "revoke",
] as const;
export type PackLedgerKind = (typeof PACK_LEDGER_KIND)[number];

export const PACK_LEDGER_STATUS = ["held", "settled", "reversed"] as const;
export type PackLedgerStatus = (typeof PACK_LEDGER_STATUS)[number];

export const PACK_BOOKING_TYPE = ["event_booking", "venue_booking"] as const;
export type PackBookingType = (typeof PACK_BOOKING_TYPE)[number];

// ── JSON shapes ───────────────────────────────────────────────────────────────

/**
 * Pause policy. Deliberately an object rather than a boolean: "may or may not be
 * able to be paused" is never really a yes/no question, and a freeze with no
 * ceiling turns a 6-month pack into a permanent one whose liability never clears.
 */
export type PackFreezePolicy = {
  /** 0 disables pausing entirely. */
  maxFreezes: number;
  /** Total frozen days across the life of one holding. */
  maxTotalDays: number;
  /** Lead time before a freeze may start. 0 = pause from now. */
  minNoticeHours: number;
};

/**
 * Rolling usage caps, evaluated against the ledger at query time — never
 * materialised counters. "10 classes, but only 1 per week."
 */
export type PackFrequencyLimits = {
  perDay?: number;
  perWeek?: number;
  perMonth?: number;
};

/**
 * The pack and its scope rows, frozen at purchase. Read in preference to the
 * live rows for everything except display of the pack's own name.
 */
export type PackSnapshot = {
  packId: number;
  name: string;
  kind: PackKind;
  redemptionMode: PackRedemptionMode | null;
  creditCount: number | null;
  clockStart: PackClockStart;
  validityMode: PackValidityMode;
  validityDays: number | null;
  freezePolicy: PackFreezePolicy | null;
  frequencyLimits: PackFrequencyLimits | null;
  cancellationWindowHours: number;
  noShowBurnsCredit: boolean;
  unitReferenceMinor: number;
  refundable: boolean;
  /**
   * The batch shape, when this is a cohort pack. Lives in the snapshot and not
   * only on `hiive_pack.metadata` because metadata is NOT snapshotted: a queen
   * reordering the curriculum would otherwise retroactively change what every
   * existing holder bought, and an enrollment already written against the old
   * shape would no longer be explicable. Null for an ordinary pack.
   *
   * Typed loosely here to keep the schema module free of a lib import; parsed
   * by `parseBatchConfig` in src/lib/packs/cohorts.ts. Optional because every
   * holding sold before this field existed genuinely does not carry it.
   */
  batch?: unknown;
  scopes: Array<{
    /** The live scope row this was copied from, so a disputed redemption can
     * be traced back to the rule that authorised it. */
    scopeId: number;
    targetType: PackScopeTarget;
    targetId: number | null;
    activityId: string | null;
    creditCost: number;
    creditUnitMinutes: number | null;
    subLimit: number | null;
    coversAddons: boolean;
    constraints: PromotionConstraints | null;
  }>;
};

// ── The sellable definition ───────────────────────────────────────────────────

export const hiivePack = sqliteTable(
  "hiive_pack",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    hiiveId: integer("hiive_id")
      .notNull()
      .references(() => hiive.id, { onDelete: "cascade" }),
    createdByUserId: text("created_by_user_id").references(() => user.id, {
      onDelete: "set null",
    }),

    name: text("name").notNull(),
    slug: text("slug").notNull(),
    description: text("description").notNull().default(""),
    status: text("status").notNull().$type<PackStatus>().default("draft"),

    kind: text("kind").notNull().$type<PackKind>().default("credit"),
    redemptionMode: text("redemption_mode").$type<PackRedemptionMode>(),

    /**
     * Credit packs only. A one-credit pack is indistinguishable from a drop-in
     * ticket and shipping both confuses everyone, so the floor is two.
     */
    creditCount: integer("credit_count"),

    windowMode: text("window_mode").$type<PackWindowMode>(),
    windowDays: integer("window_days"),
    windowStartsAt: integer("window_starts_at", { mode: "timestamp" }),
    windowEndsAt: integer("window_ends_at", { mode: "timestamp" }),

    clockStart: text("clock_start")
      .notNull()
      .$type<PackClockStart>()
      .default("purchase"),
    activeFrom: integer("active_from", { mode: "timestamp" }),
    validityMode: text("validity_mode")
      .notNull()
      .$type<PackValidityMode>()
      .default("never"),
    validityDays: integer("validity_days"),
    validUntil: integer("valid_until", { mode: "timestamp" }),

    // Stored price is ALWAYS gross, as everywhere else in the system.
    priceAmountMinor: integer("price_amount_minor").notNull().default(0),
    currency: text("currency").notNull().default("INR"),
    taxProfileId: integer("tax_profile_id").references(() => taxProfile.id, {
      onDelete: "set null",
    }),
    priceIncludesTax: integer("price_includes_tax", { mode: "boolean" })
      .notNull()
      .default(true),

    /**
     * The drop-in equivalent of one credit, frozen onto each holding at purchase.
     *
     * Without it, "refund a 10-pack with 3 used" has no answer. With it the
     * member loses the discount only on what they consumed:
     *   refundable = pricePaid − creditsUsed × unitReferenceMinor
     * It is snapshotted because a queen raising the drop-in price later must not
     * retroactively shrink everybody's refund.
     */
    unitReferenceMinor: integer("unit_reference_minor").notNull().default(0),

    freezePolicy: text("freeze_policy", {
      mode: "json",
    }).$type<PackFreezePolicy | null>(),
    frequencyLimits: text("frequency_limits", {
      mode: "json",
    }).$type<PackFrequencyLimits | null>(),

    /** Cancel at least this long before the session and the credit comes back. */
    cancellationWindowHours: integer("cancellation_window_hours")
      .notNull()
      .default(0),
    /** A no-show burns the credit. What makes the discount survivable. */
    noShowBurnsCredit: integer("no_show_burns_credit", { mode: "boolean" })
      .notNull()
      .default(true),

    onePerCustomer: integer("one_per_customer", { mode: "boolean" })
      .notNull()
      .default(false),
    newCustomersOnly: integer("new_customers_only", { mode: "boolean" })
      .notNull()
      .default(false),
    shareable: integer("shareable", { mode: "boolean" })
      .notNull()
      .default(false),
    maxSold: integer("max_sold"),

    /**
     * Per-pack opt-out of refunds. Not a `refundPolicy` row: pack refundability
     * is consumption-based, not a time ladder, so a policy scope for packs would
     * be a table of empty ladders. See quotePackRefund.
     */
    refundable: integer("refundable", { mode: "boolean" })
      .notNull()
      .default(true),

    /** Who may buy. Reuses the wallet Rule engine, as promotions and credits do. */
    eligibilityRule: text("eligibility_rule", { mode: "json" }).$type<Rule>(),

    sortOrder: integer("sort_order").notNull().default(0),
    metadata: text("metadata", { mode: "json" }).$type<
      Record<string, unknown>
    >(),
    ...timestamps,
  },
  (t) => [
    uniqueIndex("hiive_pack_slug_unique").on(t.hiiveId, t.slug),
    index("hiive_pack_by_hiive").on(t.hiiveId),
    index("hiive_pack_by_status").on(t.hiiveId, t.status),
    index("hiive_pack_sort").on(t.hiiveId, t.sortOrder),
    check("hiive_pack_kind_check", sql`${t.kind} in ('credit', 'window')`),
    check(
      "hiive_pack_price_check",
      sql`${t.priceAmountMinor} >= 0 and ${t.unitReferenceMinor} >= 0`,
    ),
    // A one-class pack is a pass type wearing a costume.
    check(
      "hiive_pack_credit_count_check",
      sql`(${t.kind} = 'credit' and ${t.creditCount} >= 2)
          or (${t.kind} = 'window' and ${t.creditCount} is null)`,
    ),
    // Any pack may say who holds the seat; a window pack MUST.
    //
    // Credit packs were originally forbidden a redemption mode, on the reading
    // that "book each session yourself" vs "the purchase books them all" was a
    // property of unlimited-in-a-window passes. A batch pass disproves it: four
    // of its five credits must land on named sessions at purchase, and the
    // fifth stays loose for a makeup class whose date does not exist yet.
    // Neither half is expressible in a window pack, which meters attendance
    // rather than counting. See 20260915130000_pack_credit_enrolled.
    check(
      "hiive_pack_redemption_mode_check",
      sql`${t.redemptionMode} is null or ${t.redemptionMode} in ('on_demand', 'enrolled')`,
    ),
    check(
      "hiive_pack_window_mode_check",
      sql`${t.kind} <> 'window' or ${t.redemptionMode} is not null`,
    ),
    check(
      "hiive_pack_validity_check",
      sql`(${t.validityMode} = 'never')
          or (${t.validityMode} = 'duration' and ${t.validityDays} >= 1)
          or (${t.validityMode} = 'fixed_date' and ${t.validUntil} is not null)`,
    ),
    check(
      "hiive_pack_clock_start_check",
      sql`${t.clockStart} <> 'fixed_date' or ${t.activeFrom} is not null`,
    ),
    check(
      "hiive_pack_cancellation_window_check",
      sql`${t.cancellationWindowHours} >= 0`,
    ),
    check(
      "hiive_pack_max_sold_check",
      sql`${t.maxSold} is null or ${t.maxSold} >= 1`,
    ),
  ],
);

// ── What it pays for ──────────────────────────────────────────────────────────

/**
 * One rule saying "this pack pays for THAT, at THIS cost". Rows OR together.
 *
 * `constraints` reuses `PromotionConstraints` rather than inventing a parallel
 * vocabulary: a pack is structurally a prepaid, metered promotion, and the
 * day/time/date fields mean exactly the same thing here. That is what makes an
 * off-peak pack — the real utilisation lever — config rather than code.
 */
export const hiivePackScope = sqliteTable(
  "hiive_pack_scope",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    packId: integer("pack_id")
      .notNull()
      .references(() => hiivePack.id, { onDelete: "cascade" }),

    targetType: text("target_type").notNull().$type<PackScopeTarget>(),
    /** Numeric target id for every targetType except `activity`. */
    targetId: integer("target_id"),
    /** Set only when targetType = 'activity'; activity ids are text. */
    activityId: text("activity_id"),

    /**
     * Credits one redemption costs. 1 for a class. 2 for a peak slot, which is
     * the ClassPass lever, and the reason this is a column rather than implied.
     *
     * ZERO means an *included entitlement*: the pack pays for this, and the
     * balance does not move. That is how a bonus is expressed — Salsa Nova's
     * batch pass is four classes plus one free place at the monthly Practice &
     * Makeup session, and the monthly is not a fifth class. Selling it as an
     * extra credit instead both misprices the pack (four classes presented as
     * five) and turns the bonus into a voucher spendable on anything in scope.
     */
    creditCost: integer("credit_cost").notNull().default(1),

    /**
     * For duration-priced (`specific_spot`, flexible_time) experiences, where a
     * slot is not a fixed unit: cost = creditCost × ceil(minutes / this).
     * A 90-minute court on a 60-minute unit is 2 credits. Integers only —
     * fractional credits are a support nightmare.
     */
    creditUnitMinutes: integer("credit_unit_minutes"),

    /** Cap within the pack: "10 overall, but at most 5 of these". */
    subLimit: integer("sub_limit"),

    /**
     * Whether the pack also pays for addons attached to the booking. Defaults
     * false: the pack covers the entry, the ₹100 shoe rental is still cash, and
     * that residual is exactly why a redemption is a tender and not a status.
     */
    coversAddons: integer("covers_addons", { mode: "boolean" })
      .notNull()
      .default(false),

    constraints: text("constraints", {
      mode: "json",
    }).$type<PromotionConstraints | null>(),

    ...timestamps,
  },
  (t) => [
    index("hiive_pack_scope_by_pack").on(t.packId),
    index("hiive_pack_scope_by_target").on(t.targetType, t.targetId),
    index("hiive_pack_scope_by_activity").on(t.activityId),
    check(
      "hiive_pack_scope_target_check",
      sql`(${t.targetType} = 'activity' and ${t.activityId} is not null and ${t.targetId} is null)
          or (${t.targetType} <> 'activity' and ${t.targetId} is not null and ${t.activityId} is null)`,
    ),
    // Zero is legal and meaningful: see `creditCost`. Negative is not — a scope
    // that hands credits back is not a pricing rule, it is a bug.
    check("hiive_pack_scope_credit_cost_check", sql`${t.creditCost} >= 0`),
    check(
      "hiive_pack_scope_credit_unit_check",
      sql`${t.creditUnitMinutes} is null or ${t.creditUnitMinutes} >= 1`,
    ),
    check(
      "hiive_pack_scope_sub_limit_check",
      sql`${t.subLimit} is null or ${t.subLimit} >= 1`,
    ),
  ],
);

// ── One customer's copy ───────────────────────────────────────────────────────

export const hiivePackHolding = sqliteTable(
  "hiive_pack_holding",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    packId: integer("pack_id")
      .notNull()
      .references(() => hiivePack.id, { onDelete: "restrict" }),
    /** Whose entitlement this is. */
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "restrict" }),
    /** Who paid. Differs from userId for a gift or a front-desk sale. */
    purchasedByUserId: text("purchased_by_user_id").references(() => user.id, {
      onDelete: "set null",
    }),

    source: text("source")
      .notNull()
      .$type<PackHoldingSource>()
      .default("purchase"),
    status: text("status")
      .notNull()
      .$type<PackHoldingStatus>()
      .default("pending"),

    /** Null for a window pack, which meters by time rather than by count. */
    creditsGranted: integer("credits_granted"),

    pricePaidMinor: integer("price_paid_minor").notNull().default(0),
    currency: text("currency").notNull().default("INR"),
    netAmountMinor: integer("net_amount_minor").notNull().default(0),
    chargeAmountMinor: integer("charge_amount_minor").notNull().default(0),
    taxAmountMinor: integer("tax_amount_minor").notNull().default(0),
    pricingSnapshot: text("pricing_snapshot", {
      mode: "json",
    }).$type<PricingSnapshot | null>(),
    taxSnapshot: text("tax_snapshot", {
      mode: "json",
    }).$type<TaxSnapshot | null>(),

    /**
     * The drop-in equivalent in force when this was bought. Frozen so a later
     * price edit cannot change the refund terms it was sold under.
     */
    unitReferenceMinor: integer("unit_reference_minor").notNull().default(0),

    paymentStatus: text("payment_status")
      .notNull()
      .$type<PackHoldingPaymentStatus>()
      .default("pending"),
    paymentProvider: text("payment_provider")
      .notNull()
      .$type<PaymentProvider>()
      .default("razorpay"),

    /**
     * Null until the first credit settles when clockStart = 'first_use'. That is
     * what lets somebody buy in December for a January start.
     */
    activatedAt: integer("activated_at", { mode: "timestamp" }),
    startsAt: integer("starts_at", { mode: "timestamp" }),
    /** NULL = never expires. Extended by freezes and by operator cancellations. */
    expiresAt: integer("expires_at", { mode: "timestamp" }),
    exhaustedAt: integer("exhausted_at", { mode: "timestamp" }),

    freezeCount: integer("freeze_count").notNull().default(0),
    frozenDaysUsed: integer("frozen_days_used").notNull().default(0),
    frozenUntil: integer("frozen_until", { mode: "timestamp" }),

    // ── Refunds ──────────────────────────────────────────────────────────────
    // These four exist because `markSourceRefundState` and `bumpSourceTotals`
    // write them onto the source table. Without them a holding cannot ride the
    // refund engine at all, and packs would need a second refund path — which is
    // precisely what this design refuses to build.
    refundPolicyId: integer("refund_policy_id"),
    refundedMinor: integer("refunded_minor").notNull().default(0),
    refundFeeMinor: integer("refund_fee_minor").notNull().default(0),
    refundState: text("refund_state")
      .notNull()
      .$type<RefundState>()
      .default("none"),

    /** The pack + its scope rows, frozen at purchase. Governs this holding. */
    snapshot: text("snapshot", { mode: "json" }).$type<PackSnapshot | null>(),

    /** Dedupe key so a retried finalize can never double-grant. */
    idempotencyKey: text("idempotency_key"),

    expiringNotifiedAt: integer("expiring_notified_at", { mode: "timestamp" }),
    lowCreditNotifiedAt: integer("low_credit_notified_at", {
      mode: "timestamp",
    }),

    metadata: text("metadata", { mode: "json" }).$type<
      Record<string, unknown>
    >(),
    ...timestamps,
  },
  (t) => [
    // Primary lookup: a member's live holdings, soonest expiry first.
    index("hiive_pack_holding_user_status_expiry_idx").on(
      t.userId,
      t.status,
      t.expiresAt,
    ),
    index("hiive_pack_holding_by_pack").on(t.packId),
    index("hiive_pack_holding_by_user").on(t.userId),
    // Expiry sweep scans by expiry across all users.
    index("hiive_pack_holding_expiry_idx").on(t.expiresAt),
    uniqueIndex("hiive_pack_holding_idempotency_unique").on(t.idempotencyKey),
    check(
      "hiive_pack_holding_credits_check",
      sql`${t.creditsGranted} is null or ${t.creditsGranted} >= 1`,
    ),
    check(
      "hiive_pack_holding_price_check",
      sql`${t.pricePaidMinor} >= 0 and ${t.refundedMinor} >= 0 and ${t.refundFeeMinor} >= 0`,
    ),
    check(
      "hiive_pack_holding_freeze_check",
      sql`${t.freezeCount} >= 0 and ${t.frozenDaysUsed} >= 0`,
    ),
  ],
);

// ── The ledger ────────────────────────────────────────────────────────────────

/**
 * Append-only. Never UPDATE a row's `credits`; correct with a further entry.
 * `status` is the one mutable field, and every change to it writes a transition.
 */
export const hiivePackLedgerEntry = sqliteTable(
  "hiive_pack_ledger_entry",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    holdingId: integer("holding_id")
      .notNull()
      .references(() => hiivePackHolding.id, { onDelete: "restrict" }),

    kind: text("kind").notNull().$type<PackLedgerKind>(),
    status: text("status").notNull().$type<PackLedgerStatus>().default("held"),

    /**
     * Signed. Negative draws down the holding, positive returns to it. Window
     * packs write 0 — they meter attendance, not credits, but still need the row
     * so frequency caps and no-show strikes have something to count.
     */
    credits: integer("credits").notNull().default(0),

    /** Rupee value of the lines this redemption covered. Reporting only. */
    coveredMinor: integer("covered_minor").notNull().default(0),

    bookingType: text("booking_type").$type<PackBookingType>(),
    bookingId: integer("booking_id"),
    /** Which scope row authorised this, for auditing a disputed redemption. */
    scopeId: integer("scope_id"),

    /** Who consumed it — differs from the holder on a shared pack. */
    redeemedForUserId: text("redeemed_for_user_id").references(() => user.id, {
      onDelete: "set null",
    }),
    /** Who performed it: the member, or front-desk staff. */
    actorUserId: text("actor_user_id").references(() => user.id, {
      onDelete: "set null",
    }),

    reason: text("reason"),
    idempotencyKey: text("idempotency_key"),
    metadata: text("metadata", { mode: "json" }).$type<
      Record<string, unknown>
    >(),
    ...timestamps,
  },
  (t) => [
    // The balance query: every consuming entry on one holding.
    index("hiive_pack_ledger_holding_status_idx").on(
      t.holdingId,
      t.status,
      t.kind,
    ),
    index("hiive_pack_ledger_by_holding").on(t.holdingId),
    // Cancel/expire paths resolve entries from a booking.
    index("hiive_pack_ledger_by_booking").on(t.bookingType, t.bookingId),
    // Frequency caps count settled entries in a rolling window.
    index("hiive_pack_ledger_created_idx").on(t.holdingId, t.createdAt),
    uniqueIndex("hiive_pack_ledger_idempotency_unique").on(t.idempotencyKey),
    check(
      "hiive_pack_ledger_booking_check",
      sql`(${t.bookingType} is null and ${t.bookingId} is null)
          or (${t.bookingType} is not null and ${t.bookingId} is not null)`,
    ),
    check("hiive_pack_ledger_covered_check", sql`${t.coveredMinor} >= 0`),
  ],
);

/** Immutable audit of every ledger status change. Mirrors the credits ledger. */
export const hiivePackLedgerTransition = sqliteTable(
  "hiive_pack_ledger_transition",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    entryId: integer("entry_id")
      .notNull()
      .references(() => hiivePackLedgerEntry.id, { onDelete: "cascade" }),
    fromStatus: text("from_status").$type<PackLedgerStatus>(),
    toStatus: text("to_status").notNull().$type<PackLedgerStatus>(),
    reason: text("reason"),
    actorUserId: text("actor_user_id").references(() => user.id, {
      onDelete: "set null",
    }),
    ...timestamps,
  },
  (t) => [
    index("hiive_pack_ledger_transition_by_entry").on(t.entryId),
    index("hiive_pack_ledger_transition_created").on(t.createdAt),
  ],
);

/**
 * One pause. Kept as rows rather than a counter so "you have 2 pauses left, 18
 * days remaining" can be shown honestly and audited when disputed.
 */
export const hiivePackFreeze = sqliteTable(
  "hiive_pack_freeze",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    holdingId: integer("holding_id")
      .notNull()
      .references(() => hiivePackHolding.id, { onDelete: "cascade" }),
    startsAt: integer("starts_at", { mode: "timestamp" }).notNull(),
    /** Intended return date. */
    endsAt: integer("ends_at", { mode: "timestamp" }).notNull(),
    /** Set when the member resumes early; the extension uses this, not endsAt. */
    resumedAt: integer("resumed_at", { mode: "timestamp" }),
    /** Days actually added to the holding's expiry. Written on resume. */
    daysExtended: integer("days_extended").notNull().default(0),
    actorUserId: text("actor_user_id").references(() => user.id, {
      onDelete: "set null",
    }),
    reason: text("reason"),
    ...timestamps,
  },
  (t) => [
    index("hiive_pack_freeze_by_holding").on(t.holdingId),
    index("hiive_pack_freeze_active_idx").on(t.holdingId, t.resumedAt),
    check("hiive_pack_freeze_window_check", sql`${t.endsAt} > ${t.startsAt}`),
    check("hiive_pack_freeze_days_check", sql`${t.daysExtended} >= 0`),
  ],
);

export type HiivePack = typeof hiivePack.$inferSelect;
export type HiivePackInsert = typeof hiivePack.$inferInsert;
export type HiivePackScope = typeof hiivePackScope.$inferSelect;
export type HiivePackScopeInsert = typeof hiivePackScope.$inferInsert;
export type HiivePackHolding = typeof hiivePackHolding.$inferSelect;
export type HiivePackHoldingInsert = typeof hiivePackHolding.$inferInsert;
export type HiivePackLedgerEntry = typeof hiivePackLedgerEntry.$inferSelect;
export type HiivePackLedgerEntryInsert =
  typeof hiivePackLedgerEntry.$inferInsert;
export type HiivePackFreeze = typeof hiivePackFreeze.$inferSelect;
export type HiivePackFreezeInsert = typeof hiivePackFreeze.$inferInsert;
